/**
 * Account Transfer Service
 * 
 * Handles complete business ownership transfers
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  AccountTransferRequest,
  TransferNotification,
  TransferLog
} from '../types/accountTransfer.types';

/**
 * Generate a secure 6-digit verification code
 */
const generateVerificationCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/**
 * Create a transfer log entry
 */
const createTransferLog = async (
  transferId: string,
  action: TransferLog['action'],
  performedBy: string,
  details: string,
  success: boolean,
  error?: string
): Promise<void> => {
  try {
    const logsRef = collection(db, 'accountTransferLogs');
    await addDoc(logsRef, {
      transferId,
      action,
      performedBy,
      performedAt: serverTimestamp(),
      details,
      success,
      error: error || null
    });
  } catch (err) {
    console.error('Failed to create transfer log:', err);
  }
};

/**
 * Create a notification for transfer events
 */
const createTransferNotification = async (
  userId: string,
  fromUserId: string,
  fromUserName: string,
  transferId: string,
  companyName: string,
  type: TransferNotification['type']
): Promise<void> => {
  try {
    const messages = {
      transfer_request: `${fromUserName} wil het eigenaarschap van ${companyName} aan jou overdragen`,
      transfer_accepted: `${fromUserName} heeft de overdracht van ${companyName} geaccepteerd`,
      transfer_declined: `${fromUserName} heeft de overdracht van ${companyName} afgewezen`,
      transfer_completed: `De overdracht van ${companyName} is voltooid`
    };

    const notificationsRef = collection(db, 'notifications');
    await addDoc(notificationsRef, {
      type,
      userId,
      fromUserId,
      fromUserName,
      transferId,
      companyName,
      message: messages[type],
      read: false,
      createdAt: serverTimestamp()
    });
  } catch (err) {
    console.error('Failed to create transfer notification:', err);
  }
};

/**
 * Initiate an account transfer request
 */
export const createAccountTransferRequest = async (
  currentOwnerId: string,
  currentOwnerName: string,
  currentOwnerEmail: string,
  newOwnerEmail: string, // We'll look up the user by email
  companyName: string,
  companyKvK: string,
  transferReason: string,
  entityIds: string[],
  notes?: string
): Promise<string> => {
  try {
    console.log('🔄 Creating account transfer request...');

    // Find the new owner by email
    const usersRef = collection(db, 'userProfiles');
    const userQuery = query(usersRef, where('email', '==', newOwnerEmail));
    const userSnapshot = await getDocs(userQuery);

    if (userSnapshot.empty) {
      throw new Error('Gebruiker niet gevonden met dit e-mailadres');
    }

    const newOwnerDoc = userSnapshot.docs[0];
    const newOwnerData = newOwnerDoc.data();
    const newOwnerId = newOwnerDoc.id;

    // Generate verification code
    const verificationCode = generateVerificationCode();

    // Create transfer request
    const transferData: Omit<AccountTransferRequest, 'id'> = {
      currentOwnerId,
      currentOwnerName,
      currentOwnerEmail,
      newOwnerId,
      newOwnerName: newOwnerData.displayName || newOwnerData.firstName || 'Nieuwe eigenaar',
      newOwnerEmail,
      companyName,
      companyKvK,
      transferReason,
      includesManagers: true,
      includesVAs: true,
      includesBoardMembers: true,
      includesEntities: entityIds,
      includesContent: true,
      includesGoals: true, // Always include goals in transfer
      status: 'pending',
      requestedAt: Timestamp.now() as any,
      verificationCode,
      currentOwnerConfirmed: true, // Auto-confirmed since they created it
      newOwnerConfirmed: false,
      notes
    };

    const transfersRef = collection(db, 'accountTransfers');
    const transferDoc = await addDoc(transfersRef, transferData);

    // Create log
    await createTransferLog(
      transferDoc.id,
      'created',
      currentOwnerId,
      `Transfer request created for ${companyName}`,
      true
    );

    // Send notification to new owner
    await createTransferNotification(
      newOwnerId,
      currentOwnerId,
      currentOwnerName,
      transferDoc.id,
      companyName,
      'transfer_request'
    );

    console.log('✅ Transfer request created:', transferDoc.id);
    return transferDoc.id;
  } catch (error) {
    console.error('❌ Error creating transfer request:', error);
    throw error;
  }
};

/**
 * Accept a transfer request
 */
export const acceptTransferRequest = async (
  transferId: string,
  newOwnerId: string,
  verificationCode: string
): Promise<void> => {
  try {
    console.log('✅ Accepting transfer request:', transferId);

    const transferRef = doc(db, 'accountTransfers', transferId);
    const transferSnap = await getDoc(transferRef);

    if (!transferSnap.exists()) {
      throw new Error('Transfer aanvraag niet gevonden');
    }

    const transferData = transferSnap.data() as AccountTransferRequest;

    // Verify
    if (transferData.newOwnerId !== newOwnerId) {
      throw new Error('Niet geautoriseerd');
    }

    if (transferData.verificationCode !== verificationCode) {
      throw new Error('Onjuiste verificatiecode');
    }

    if (transferData.status !== 'pending') {
      throw new Error('Transfer is niet meer in behandeling');
    }

    // Update transfer status
    await updateDoc(transferRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      newOwnerConfirmed: true
    });

    // Create log
    await createTransferLog(
      transferId,
      'accepted',
      newOwnerId,
      `Transfer accepted by new owner`,
      true
    );

    // Notify current owner
    await createTransferNotification(
      transferData.currentOwnerId,
      newOwnerId,
      transferData.newOwnerName,
      transferId,
      transferData.companyName,
      'transfer_accepted'
    );

    // Now execute the actual transfer
    await executeTransfer(transferId);

    console.log('✅ Transfer accepted and executed');
  } catch (error) {
    console.error('❌ Error accepting transfer:', error);
    await createTransferLog(
      transferId,
      'failed',
      newOwnerId,
      `Failed to accept transfer: ${error}`,
      false,
      String(error)
    );
    throw error;
  }
};

/**
 * Decline a transfer request
 */
export const declineTransferRequest = async (
  transferId: string,
  newOwnerId: string,
  reason: string
): Promise<void> => {
  try {
    console.log('❌ Declining transfer request:', transferId);

    const transferRef = doc(db, 'accountTransfers', transferId);
    const transferSnap = await getDoc(transferRef);

    if (!transferSnap.exists()) {
      throw new Error('Transfer aanvraag niet gevonden');
    }

    const transferData = transferSnap.data() as AccountTransferRequest;

    if (transferData.newOwnerId !== newOwnerId) {
      throw new Error('Niet geautoriseerd');
    }

    // Update transfer status
    await updateDoc(transferRef, {
      status: 'declined',
      declinedAt: serverTimestamp(),
      declineReason: reason
    });

    // Create log
    await createTransferLog(
      transferId,
      'declined',
      newOwnerId,
      `Transfer declined: ${reason}`,
      true
    );

    // Notify current owner
    await createTransferNotification(
      transferData.currentOwnerId,
      newOwnerId,
      transferData.newOwnerName,
      transferId,
      transferData.companyName,
      'transfer_declined'
    );

    console.log('✅ Transfer declined');
  } catch (error) {
    console.error('❌ Error declining transfer:', error);
    throw error;
  }
};

/**
 * Execute the actual transfer (called after acceptance)
 */
const executeTransfer = async (transferId: string): Promise<void> => {
  try {
    console.log('🔄 Executing transfer:', transferId);

    const transferRef = doc(db, 'accountTransfers', transferId);
    const transferSnap = await getDoc(transferRef);

    if (!transferSnap.exists()) {
      throw new Error('Transfer not found');
    }

    const transfer = transferSnap.data() as AccountTransferRequest;
    const batch = writeBatch(db);

    // 1. Transfer all manager relations
    if (transfer.includesManagers) {
      const managersRef = collection(db, 'companyManagerRelations');
      const managersQuery = query(managersRef, where('companyOwnerId', '==', transfer.currentOwnerId));
      const managersSnap = await getDocs(managersQuery);

      managersSnap.docs.forEach((doc) => {
        batch.update(doc.ref, {
          companyOwnerId: transfer.newOwnerId,
          transferredAt: serverTimestamp(),
          transferredFrom: transfer.currentOwnerId
        });
      });
    }

    // 2. Transfer goals/doelstellingen
    const goalsRef = collection(db, 'goals');
    const goalsQuery = query(goalsRef, where('ownerId', '==', transfer.currentOwnerId));
    const goalsSnap = await getDocs(goalsQuery);

    goalsSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        ownerId: transfer.newOwnerId,
        transferredAt: serverTimestamp(),
        transferredFrom: transfer.currentOwnerId
      });
    });

    // Also transfer goals data from profiles collection
    const currentProfileRef = doc(db, 'profiles', transfer.currentOwnerId);
    const newProfileRef = doc(db, 'profiles', transfer.newOwnerId);
    const currentProfileSnap = await getDoc(currentProfileRef);
    
    if (currentProfileSnap.exists()) {
      const currentProfileData = currentProfileSnap.data();
      const goalsData = {
        goals: currentProfileData.goals || {},
        learningGoals: currentProfileData.learningGoals || [],
        milestones: currentProfileData.milestones || []
      };
      
      // Merge goals into new owner's profile
      batch.set(newProfileRef, goalsData, { merge: true });
    }

    // 3. Transfer entity ownership
    for (const entityId of transfer.includesEntities) {
      const entityRef = doc(db, 'entities', entityId);
      batch.update(entityRef, {
        ownerId: transfer.newOwnerId,
        transferredAt: serverTimestamp(),
        transferredFrom: transfer.currentOwnerId
      });

      // Update entity members
      const membersRef = collection(db, 'entityMembers');
      const membersQuery = query(
        membersRef,
        where('entityId', '==', entityId),
        where('userId', '==', transfer.currentOwnerId)
      );
      const membersSnap = await getDocs(membersQuery);

      membersSnap.docs.forEach((doc) => {
        batch.update(doc.ref, {
          role: 'member', // Downgrade old owner to member
          updatedAt: serverTimestamp()
        });
      });

      // Add new owner as owner
      const newOwnerMemberRef = doc(collection(db, 'entityMembers'));
      batch.set(newOwnerMemberRef, {
        entityId,
        userId: transfer.newOwnerId,
        role: 'owner',
        joinedAt: serverTimestamp(),
        addedBy: transfer.currentOwnerId
      });
    }

    // 4. Transfer ALL content and data (everything stays as-is, only ownership changes)
    
    // Community posts
    const postsRef = collection(db, 'communityPosts');
    const postsQuery = query(postsRef, where('authorId', '==', transfer.currentOwnerId));
    const postsSnap = await getDocs(postsQuery);
    postsSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        authorId: transfer.newOwnerId,
        transferredAt: serverTimestamp(),
        originalAuthor: transfer.currentOwnerId
      });
    });

    // Agenda tasks
    const agendaTasksRef = collection(db, 'agendaTasks');
    const agendaQuery = query(agendaTasksRef, where('userId', '==', transfer.currentOwnerId));
    const agendaSnap = await getDocs(agendaQuery);
    agendaSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Time tracking sessions
    const timeTrackingRef = collection(db, 'timeTrackingSessions');
    const timeQuery = query(timeTrackingRef, where('userId', '==', transfer.currentOwnerId));
    const timeSnap = await getDocs(timeQuery);
    timeSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Calendar connections (keep same calendar, just update userId)
    const calendarRef = collection(db, 'calendarConnections');
    const calendarQuery = query(calendarRef, where('userId', '==', transfer.currentOwnerId));
    const calendarSnap = await getDocs(calendarQuery);
    calendarSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Notifications (keep all notifications as-is)
    const notificationsRef = collection(db, 'notifications');
    const notifQuery = query(notificationsRef, where('userId', '==', transfer.currentOwnerId));
    const notifSnap = await getDocs(notifQuery);
    notifSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Inbox messages
    const inboxRef = collection(db, 'inbox');
    const inboxQuery = query(inboxRef, where('userId', '==', transfer.currentOwnerId));
    const inboxSnap = await getDocs(inboxQuery);
    inboxSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Connection requests (sent and received)
    const connRequestsRef = collection(db, 'connectionRequests');
    const sentConnQuery = query(connRequestsRef, where('fromUserId', '==', transfer.currentOwnerId));
    const sentConnSnap = await getDocs(sentConnQuery);
    sentConnSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        fromUserId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    const receivedConnQuery = query(connRequestsRef, where('toUserId', '==', transfer.currentOwnerId));
    const receivedConnSnap = await getDocs(receivedConnQuery);
    receivedConnSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        toUserId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Chat rooms (update participant IDs)
    const chatRoomsRef = collection(db, 'chatRooms');
    const chatQuery = query(chatRoomsRef, where('participants', 'array-contains', transfer.currentOwnerId));
    const chatSnap = await getDocs(chatQuery);
    chatSnap.docs.forEach((doc) => {
      const data = doc.data();
      const updatedParticipants = data.participants.map((p: string) => 
        p === transfer.currentOwnerId ? transfer.newOwnerId : p
      );
      batch.update(doc.ref, {
        participants: updatedParticipants,
        transferredAt: serverTimestamp()
      });
    });

    // Meeting proposals (sent and received)
    const meetingsRef = collection(db, 'meetingProposals');
    const sentMeetingsQuery = query(meetingsRef, where('fromUserId', '==', transfer.currentOwnerId));
    const sentMeetingsSnap = await getDocs(sentMeetingsQuery);
    sentMeetingsSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        fromUserId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    const receivedMeetingsQuery = query(meetingsRef, where('toUserId', '==', transfer.currentOwnerId));
    const receivedMeetingsSnap = await getDocs(receivedMeetingsQuery);
    receivedMeetingsSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        toUserId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // User events
    const userEventsRef = collection(db, 'userEvents');
    const eventsQuery = query(userEventsRef, where('userId', '==', transfer.currentOwnerId));
    const eventsSnap = await getDocs(eventsQuery);
    eventsSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Expert status (if any)
    const expertStatusRef = doc(db, 'expertStatus', transfer.currentOwnerId);
    const expertSnap = await getDoc(expertStatusRef);
    if (expertSnap.exists()) {
      const newExpertRef = doc(db, 'expertStatus', transfer.newOwnerId);
      batch.set(newExpertRef, {
        ...expertSnap.data(),
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp(),
        transferredFrom: transfer.currentOwnerId
      });
      // Keep old one for history
    }

    // Referral codes
    const referralCodesRef = collection(db, 'referralCodes');
    const referralQuery = query(referralCodesRef, where('userId', '==', transfer.currentOwnerId));
    const referralSnap = await getDocs(referralQuery);
    referralSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // Analytics data (keep all history)
    const analyticsSessionsRef = collection(db, 'analytics_sessions');
    const analyticsQuery = query(analyticsSessionsRef, where('userId', '==', transfer.currentOwnerId));
    const analyticsSnap = await getDocs(analyticsQuery);
    analyticsSnap.docs.forEach((doc) => {
      batch.update(doc.ref, {
        userId: transfer.newOwnerId,
        transferredAt: serverTimestamp()
      });
    });

    // 5. Complete Profile Transfer
    // Get OLD owner's complete profile
    const oldOwnerProfileRef = doc(db, 'userProfiles', transfer.currentOwnerId);
    const oldProfileSnap = await getDoc(oldOwnerProfileRef);
    
    if (oldProfileSnap.exists()) {
      const oldProfileData = oldProfileSnap.data();
      const newOwnerProfileRef = doc(db, 'userProfiles', transfer.newOwnerId);
      const newProfileSnap = await getDoc(newOwnerProfileRef);
      const newProfileData = newProfileSnap.exists() ? newProfileSnap.data() : {};
      
      // Merge EVERYTHING from old profile to new profile
      // Keep new owner's name and email, transfer everything else
      batch.set(newOwnerProfileRef, {
        ...oldProfileData, // All settings, preferences, data from old owner
        
        // Keep new owner's personal identity
        firstName: newProfileData.firstName || transfer.newOwnerName.split(' ')[0],
        lastName: newProfileData.lastName || transfer.newOwnerName.split(' ').slice(1).join(' '),
        displayName: transfer.newOwnerName,
        email: transfer.newOwnerEmail,
        
        // Update ownership markers
        isCompanyOwner: true,
        becameOwnerAt: serverTimestamp(),
        previousOwner: transfer.currentOwnerId,
        transferredAt: serverTimestamp(),
        
        // Keep company info
        company: transfer.companyName,
        networkclubKvk: transfer.companyKvK
      }, { merge: true });

      // Update old owner - keep their data but mark as transferred
      batch.update(oldOwnerProfileRef, {
        isCompanyOwner: false,
        transferredOwnershipAt: serverTimestamp(),
        transferredTo: transfer.newOwnerId,
        formerCompany: transfer.companyName,
        accountTransferred: true
      });
    }

    // Also transfer the profiles collection data
    const oldProfilesRef = doc(db, 'profiles', transfer.currentOwnerId);
    const oldProfilesSnap = await getDoc(oldProfilesRef);
    
    if (oldProfilesSnap.exists()) {
      const newProfilesRef = doc(db, 'profiles', transfer.newOwnerId);
      batch.set(newProfilesRef, {
        ...oldProfilesSnap.data(),
        transferredAt: serverTimestamp(),
        transferredFrom: transfer.currentOwnerId
      }, { merge: true });
    }

    // 7. Mark transfer as completed
    batch.update(transferRef, {
      status: 'completed',
      completedAt: serverTimestamp()
    });

    // Commit all changes
    await batch.commit();

    // Create completion log
    await createTransferLog(
      transferId,
      'completed',
      transfer.newOwnerId,
      `Transfer completed successfully`,
      true
    );

    // Notify both parties
    await createTransferNotification(
      transfer.currentOwnerId,
      transfer.newOwnerId,
      transfer.newOwnerName,
      transferId,
      transfer.companyName,
      'transfer_completed'
    );

    await createTransferNotification(
      transfer.newOwnerId,
      transfer.currentOwnerId,
      transfer.currentOwnerName,
      transferId,
      transfer.companyName,
      'transfer_completed'
    );

    console.log('✅ Transfer executed successfully');
  } catch (error) {
    console.error('❌ Error executing transfer:', error);
    await createTransferLog(
      transferId,
      'failed',
      'system',
      `Failed to execute transfer: ${error}`,
      false,
      String(error)
    );
    throw error;
  }
};

/**
 * Cancel a pending transfer (only by current owner)
 */
export const cancelTransferRequest = async (
  transferId: string,
  currentOwnerId: string
): Promise<void> => {
  try {
    const transferRef = doc(db, 'accountTransfers', transferId);
    const transferSnap = await getDoc(transferRef);

    if (!transferSnap.exists()) {
      throw new Error('Transfer niet gevonden');
    }

    const transferData = transferSnap.data() as AccountTransferRequest;

    if (transferData.currentOwnerId !== currentOwnerId) {
      throw new Error('Niet geautoriseerd');
    }

    if (transferData.status !== 'pending') {
      throw new Error('Kan alleen pending transfers annuleren');
    }

    await updateDoc(transferRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp()
    });

    await createTransferLog(
      transferId,
      'cancelled',
      currentOwnerId,
      'Transfer cancelled by current owner',
      true
    );

    console.log('✅ Transfer cancelled');
  } catch (error) {
    console.error('❌ Error cancelling transfer:', error);
    throw error;
  }
};

/**
 * Get pending transfers for a user (either as sender or receiver)
 */
export const getUserTransfers = async (userId: string): Promise<AccountTransferRequest[]> => {
  try {
    const transfersRef = collection(db, 'accountTransfers');
    
    // Get transfers where user is current owner
    const sentQuery = query(transfersRef, where('currentOwnerId', '==', userId));
    const sentSnap = await getDocs(sentQuery);
    
    // Get transfers where user is new owner
    const receivedQuery = query(transfersRef, where('newOwnerId', '==', userId));
    const receivedSnap = await getDocs(receivedQuery);
    
    const transfers: AccountTransferRequest[] = [];
    
    sentSnap.docs.forEach(doc => {
      transfers.push({ id: doc.id, ...doc.data() } as AccountTransferRequest);
    });
    
    receivedSnap.docs.forEach(doc => {
      transfers.push({ id: doc.id, ...doc.data() } as AccountTransferRequest);
    });
    
    return transfers;
  } catch (error) {
    console.error('Error fetching transfers:', error);
    return [];
  }
};
